/**
 * Tangible header refresh signal (#1028).
 *
 * TangibleDisplay fetches /tangible once per group change, so mid-session line
 * mutations leave the header metrics stale. Until the on-demand caching work
 * lands, line create/edit/delete dispatches this event and TangibleDisplay
 * refetches. Interface is one event name + one notifier so a future cache
 * layer can replace the internals without touching call sites.
 */

export const TANGIBLE_REFRESH_EVENT = 'budgetrecon:tangible-refresh';

export const notifyTangibleRefresh = () => {
  window.dispatchEvent(new CustomEvent(TANGIBLE_REFRESH_EVENT));
};

/**
 * Reload delays after a refresh event, in ms. Readers of stream-maintained
 * rows (the balance cache behind /tangible, #857; the flows month buckets
 * behind /flows, #1940) see a write's delta asynchronously: the lines stream
 * event source batches up to 5s (MaximumBatchingWindowInSeconds) and the
 * processor can cold-start on a low-traffic env, so the delta typically lands
 * ~4-5s out, sometimes more. A fixed backoff brackets that window, then stops
 * (#1104).
 */
export const TANGIBLE_REFRESH_BACKOFF_MS = [1000, 3000, 6000, 10000, 20000, 30000];

/**
 * Run `reload` on every refresh event: once at once, then once per backoff
 * delay. Returns the unsubscribe, which also cancels pending reloads.
 * @param {function} reload
 * @returns {function} unsubscribe
 */
export const subscribeTangibleRefresh = (reload) => {
  const timers = new Set();
  const onRefresh = () => {
    reload();
    TANGIBLE_REFRESH_BACKOFF_MS.forEach((delay) => {
      const t = setTimeout(() => {
        timers.delete(t);
        reload();
      }, delay);
      timers.add(t);
    });
  };
  window.addEventListener(TANGIBLE_REFRESH_EVENT, onRefresh);
  return () => {
    timers.forEach((t) => clearTimeout(t));
    window.removeEventListener(TANGIBLE_REFRESH_EVENT, onRefresh);
  };
};
